import { PhoneNumberType } from '../../common/enums';
import { SalesType } from '../../generated/api/salesType';
import { getAddToCartAnalyticsEvent } from '../../common/utils/analyticsUtils';
import { getAddedAddOns } from '../SubscriptionDetails/addOnDependencyUtils';
import { getCampaignPromotion, getOfferCampaignAssociationAndPromotion } from '../../common/utils/campaignUtils';
import { getLaitenettiOfferByName, getOffer } from './SubscriptionUtils';
import { getSelectedServiceFee } from '../OrderSubscriptionSelection/OrderSubscriptionSelectionUtils';
import { pushToDataLayer } from '../../common/analytics';
import type { AddOnRulesResponse } from '../../generated/api/addOnRulesResponse';
import type { AssociationRecord } from '@onlinefirst/cloudsense-add-on-dependency-engine';
import type { CampaignContext } from '../../generated/api/campaignContext';
import type { CampaignPromotion } from '../../generated/api/campaignPromotion';
import type { NavigateFunction } from 'react-router-dom';
import type { Offer } from '../../generated/api/offer';
import type { OfferProductMapType } from '../OrderSubscriptionSelection/OrderSubscriptionSelectionUtils';
import type { OnlineModel } from '../../generated/api/onlineModel';
import type { SelectedPhoneNumber } from '../../common/types/subscription';
import type { Subscription } from '../../generated/api/subscription';
import type { SubscriptionUpdateNavigationState } from '../../public/site/path/SelfService/ProductsAndServices/MobileSubUpdateConfirmationPath';

interface OrderHandlerOptions {
  onlineModel: OnlineModel;
  navigate: NavigateFunction;
  search: string;
  isChangeOffer: boolean;

  isNewNumber?: boolean;
  quantity?: number;
  voucherCode?: string;
  services?: Array<{ isIncluded?: boolean; guid?: string }>;
  offerAndFeeMap?: OfferProductMapType[];

  campaignsFromVoucher: CampaignPromotion[];
  campaignContextsFromVoucher: CampaignContext[];

  laitenettiOfferName?: string; // For laitenetti
  noeOfferCode?: string; // For mobile broadband
  offers?: Array<{ guid?: string }>; // For voice subscriptions

  additionalOffers?: Offer[];
  addOnRules?: AddOnRulesResponse;
  subscription?: Subscription; // Current subscription for change offer

  // Navigation paths
  confirmationPath: string;
  productInfoPath: string;
}

export const handleOrder = ({
  onlineModel,
  navigate,
  search,
  isChangeOffer,
  isNewNumber,
  quantity = 1,
  voucherCode,
  services = [],
  offerAndFeeMap = [],
  campaignsFromVoucher = [],
  campaignContextsFromVoucher = [],
  noeOfferCode,
  laitenettiOfferName,
  offers,
  additionalOffers = [],
  addOnRules,
  subscription,
  confirmationPath,
  productInfoPath,
}: OrderHandlerOptions) => {
  // Get the appropriate offer
  const allOffers = [...(onlineModel.offers || []), ...additionalOffers];

  // Determine which offer code to use based on what's provided
  const offerToFind = noeOfferCode || offers?.[0]?.guid;

  const offer = laitenettiOfferName
    ? getLaitenettiOfferByName(laitenettiOfferName, allOffers)
    : getOffer(offerToFind, allOffers);

  if (!offer) {
    return;
  }

  const phoneNumberType = isNewNumber ? PhoneNumberType.NEW : PhoneNumberType.EXISTING;
  const selectedPhoneNumber: SelectedPhoneNumber | undefined = isNewNumber ? { type: phoneNumberType } : undefined;
  const selectedServiceFee = getSelectedServiceFee(offerAndFeeMap, offer.commercialProducts[0].commercialProductId);

  // Handle campaign associations and promotions
  const campaignPromotions = [...campaignsFromVoucher, ...(onlineModel.campaigns || [])];
  const offerCampaignAssociationAndPromotion = getOfferCampaignAssociationAndPromotion(
    offer,
    isChangeOffer ? SalesType.CHANGE_OFFER : SalesType.NEW_SALE,
    campaignContextsFromVoucher,
    voucherCode,
    campaignPromotions
  );

  const offerCampaignAssociation = offerCampaignAssociationAndPromotion?.campaignAssociation;
  let campaignDescription: string | undefined;
  let campaignName: string | undefined;

  if (offerCampaignAssociation) {
    const campaignPromotion = getCampaignPromotion(campaignPromotions, offerCampaignAssociation.campaignPromotionCode);
    campaignDescription = campaignPromotion?.description;
    campaignName = campaignPromotion?.name;
  }

  // Push analytics data
  pushToDataLayer(
    getAddToCartAnalyticsEvent(
      onlineModel.onlineModelName,
      offer,
      quantity,
      [], // Addons will be added later
      voucherCode,
      offerCampaignAssociation
    )
  );

  // Process included services
  const includedServices = services?.filter(s => s.isIncluded && s.guid !== undefined).map(s => s.guid as string) ?? [];

  // Get add-ons if addOnRules are provided
  const addOnAssociations = addOnRules?.associations?.[0] as AssociationRecord[];
  const selectedAddons = addOnAssociations ? getAddedAddOns(includedServices, offer, addOnAssociations) : undefined;

  // Navigate based on whether this is a change offer or not
  if (isChangeOffer) {
    navigate(confirmationPath + search, {
      state: {
        selectedAddOns: selectedAddons,
        selectedCommercialProduct: { ...offer.commercialProducts[0], oneTimeCharge: 0 },
        subscription: subscription,
        campaignAssociation: offerCampaignAssociation,
        campaignDescription: campaignDescription,
        selectedServiceFee,
      } as SubscriptionUpdateNavigationState,
    });
  } else {
    navigate(productInfoPath + search, {
      state: {
        selectedOffer: {
          offer: offer,
          onlineModelCode: onlineModel?.onlineModelCode,
          onlineModelName: onlineModel?.onlineModelName,
          selectedCommercialProducts: Array.from(Array(quantity).keys()).map(() => ({
            addedAddOns: selectedAddons || [],
            commercialProduct: offer.commercialProducts[0],
            purposeOfUseOrContact: {},
            selectedCampaignAssociation: offerCampaignAssociation,
            campaignDescription,
            campaignName,
            selectedPhoneNumber,
          })),
          selectedCampaignAssociation: offerCampaignAssociation,
          voucher: voucherCode,
        },
        addOns: onlineModel?.addOns,
      },
    });
  }
};
